/**
 * Entry point da SPA — task_019 (Roteamento completo).
 *
 * Conteúdo:
 *   1. Define TODAS as rotas da aplicação via `createBrowserRouter` (§7).
 *   2. Usa lazy loading (`React.lazy` + `<Suspense>`) para cada página, de
 *      forma que o bundle inicial seja apenas o shell + Login.
 *   3. Envolve a árvore com `<App />` (providers globais — ver `src/app.tsx`).
 *   4. Monta no `#root` definido em `index.html`.
 *
 * Estrutura de rotas (mapeada de §7 da spec):
 *   • /login                              → LoginPage (sem AppShell)
 *   • /                                   → DashboardPage  ┐
 *   • /test-cases ... /voice-samples      → tenant-pages    │ ProtectedRoute
 *   • /admin/*                            → AdminGuard      │ + AppShell
 *   • *  (catch-all)                      → redirect para / ┘
 *
 * Por que `createBrowserRouter` (e não `<BrowserRouter>` JSX):
 *   • É a API recomendada do React Router v6.4+ (suporta data routers,
 *     loaders, actions e v7_* future flags com migração mais limpa).
 *   • Permite definir rotas como uma estrutura declarativa (objeto), o que
 *     facilita testes (criar `createMemoryRouter` com as MESMAS rotas).
 *
 * Por que lazy loading aqui (e não em `app.tsx`):
 *   • As rotas SÃO a unidade natural de code-splitting; cada página vira
 *     um chunk próprio. O Vite gera automaticamente `dist/assets/<Page>-<hash>.js`.
 */
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'

import App from '@/app'
import { AdminGuard } from '@/components/layout/AdminGuard'
import { AppShell } from '@/components/layout/AppShell'
import { PermissionRoute } from '@/components/layout/PermissionRoute'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'

import '@/styles/globals.css'

// ───────────────────────────────────────────────────────────────────────────
// Lazy-loaded pages — cada `lazy()` gera um chunk próprio no build de prod.
// O `default` export de cada módulo é o componente da página.
// ───────────────────────────────────────────────────────────────────────────

const LLMPage = React.lazy(() => import('@/pages/admin/LLMPage'))

const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'))
const DashboardPage = React.lazy(() => import('@/pages/dashboard/DashboardPage'))
const ObservabilityPage = React.lazy(() => import('@/pages/observability/ObservabilityPage'))

const TestCaseListPage = React.lazy(() =>
  import('@/pages/test-cases/TestCaseListPage').then((m) => ({
    default: m.TestCaseListPage,
  })),
)
const TestCaseEditorPage = React.lazy(() =>
  import('@/pages/test-cases/TestCaseEditorPage').then((m) => ({
    default: m.TestCaseEditorPage,
  })),
)
const TestCaseVersionsPage = React.lazy(() =>
  import('@/pages/test-cases/TestCaseVersionsPage').then((m) => ({
    default: m.TestCaseVersionsPage,
  })),
)

const TestSuiteListPage = React.lazy(() => import('@/pages/test-suites/TestSuiteListPage'))
const TestSuiteEditorPage = React.lazy(() => import('@/pages/test-suites/TestSuiteEditorPage'))
const TestSuiteVersionsPage = React.lazy(() => import('@/pages/test-suites/TestSuiteVersionsPage'))
const SuiteRunDetailPage = React.lazy(() => import('@/pages/test-suites/SuiteRunDetailPage'))

const DatasetListPage = React.lazy(() =>
  import('@/pages/datasets/DatasetListPage').then((m) => ({ default: m.DatasetListPage })),
)
const DatasetEditorPage = React.lazy(() =>
  import('@/pages/datasets/DatasetEditorPage').then((m) => ({ default: m.DatasetEditorPage })),
)
const DatasetVersionsPage = React.lazy(() =>
  import('@/pages/datasets/DatasetVersionsPage').then((m) => ({ default: m.DatasetVersionsPage })),
)
const DatasetImportPage = React.lazy(() =>
  import('@/pages/datasets/DatasetImportPage').then((m) => ({ default: m.DatasetImportPage })),
)

const CuradoriaPage = React.lazy(() =>
  import('@/pages/curadoria/CuradoriaPage').then((m) => ({
    default: m.CuradoriaPage,
  })),
)
const PainelPage = React.lazy(() =>
  import('@/pages/curadoria/PainelPage').then((m) => ({
    default: m.PainelPage,
  })),
)
const FilaAdjudicacaoPage = React.lazy(() =>
  import('@/pages/curadoria/FilaAdjudicacaoPage').then((m) => ({
    default: m.FilaAdjudicacaoPage,
  })),
)
const DocumentoPage = React.lazy(() =>
  import('@/pages/curadoria/DocumentoPage').then((m) => ({
    default: m.DocumentoPage,
  })),
)
const DocumentosPage = React.lazy(() =>
  import('@/pages/curadoria/DocumentosPage').then((m) => ({
    default: m.DocumentosPage,
  })),
)
const EstruturaPage = React.lazy(() =>
  import('@/pages/curadoria/EstruturaPage').then((m) => ({
    default: m.EstruturaPage,
  })),
)
const FilaAprovacaoPage = React.lazy(() =>
  import('@/pages/curadoria/FilaAprovacaoPage').then((m) => ({
    default: m.FilaAprovacaoPage,
  })),
)
const RevisaoPage = React.lazy(() =>
  import('@/pages/curadoria/RevisaoPage').then((m) => ({
    default: m.RevisaoPage,
  })),
)
const CorpusPage = React.lazy(() =>
  import('@/pages/curadoria/CorpusPage').then((m) => ({
    default: m.CorpusPage,
  })),
)
const ProjecoesPage = React.lazy(() =>
  import('@/pages/curadoria/ProjecoesPage').then((m) => ({
    default: m.ProjecoesPage,
  })),
)
const FunctionsPage = React.lazy(() =>
  import('@/pages/functions/FunctionsPage').then((m) => ({
    default: m.FunctionsPage,
  })),
)

const AppProfilesPage = React.lazy(() =>
  import('@/pages/app-profiles/AppProfilesPage').then((m) => ({
    default: m.AppProfilesPage,
  })),
)
const JourneyProfilesPage = React.lazy(() =>
  import('@/pages/journey-profiles/JourneyProfilesPage').then((m) => ({
    default: m.JourneyProfilesPage,
  })),
)

const DevicesPage = React.lazy(() =>
  import('@/pages/devices/DevicesPage').then((m) => ({
    default: m.DevicesPage,
  })),
)

const SchedulesPage = React.lazy(() =>
  import('@/pages/schedules/SchedulesPage').then((m) => ({
    default: m.SchedulesPage,
  })),
)

const ExecutionListPage = React.lazy(() =>
  import('@/pages/executions/ExecutionListPage').then((m) => ({
    default: m.ExecutionListPage,
  })),
)
const ExecutionDetailPage = React.lazy(() => import('@/pages/executions/ExecutionDetailPage'))

const PreviewPage = React.lazy(() =>
  import('@/pages/preview/PreviewPage').then((m) => ({
    default: m.PreviewPage,
  })),
)

const BillingPage = React.lazy(() =>
  import('@/pages/billing/BillingPage').then((m) => ({
    default: m.BillingPage,
  })),
)

const UsersPage = React.lazy(() =>
  import('@/pages/settings/UsersPage').then((m) => ({
    default: m.UsersPage,
  })),
)
const ProfilesPage = React.lazy(() =>
  import('@/pages/settings/ProfilesPage').then((m) => ({
    default: m.ProfilesPage,
  })),
)
const ApiKeysPage = React.lazy(() =>
  import('@/pages/settings/ApiKeysPage').then((m) => ({
    default: m.ApiKeysPage,
  })),
)

const VoiceSamplesPage = React.lazy(() =>
  import('@/pages/voice-samples/VoiceSamplesPage').then((m) => ({
    default: m.VoiceSamplesPage,
  })),
)

const OrganizationsPage = React.lazy(() =>
  import('@/pages/admin/OrganizationsPage').then((m) => ({
    default: m.OrganizationsPage,
  })),
)
const TenantsPage = React.lazy(() =>
  import('@/pages/admin/TenantsPage').then((m) => ({
    default: m.TenantsPage,
  })),
)
const WorkersPage = React.lazy(() =>
  import('@/pages/admin/WorkersPage').then((m) => ({
    default: m.WorkersPage,
  })),
)
const AdminBillingPage = React.lazy(() =>
  import('@/pages/admin/AdminBillingPage').then((m) => ({
    default: m.AdminBillingPage,
  })),
)
const AdminUsersPage = React.lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({
    default: m.AdminUsersPage,
  })),
)
const AccessEventsPage = React.lazy(() =>
  import('@/pages/admin/AccessEventsPage').then((m) => ({
    default: m.AccessEventsPage,
  })),
)

const ImportPage = React.lazy(() => import('@/pages/imports/ImportPage'))
const ImportTestCasesPage = React.lazy(() => import('@/pages/imports/ImportTestCasesPage'))
const ImportTestSuitePage = React.lazy(() => import('@/pages/imports/ImportTestSuitePage'))
const ImportFreeTextPage = React.lazy(() => import('@/pages/imports/ImportFreeTextPage'))

// ───────────────────────────────────────────────────────────────────────────
// Fallback global de Suspense para code-splitting das páginas.
//
// Mantemos minimalista — uma tela em branco com pequena indicação textual.
// Skeletons específicos ficam dentro de cada página (DataTable.isLoading,
// LoadingSkeleton em listagens, etc.), já que dependem do layout interno.
// ───────────────────────────────────────────────────────────────────────────

function RouteFallback(): React.ReactElement {
  return (
    <div
      data-testid="route-fallback"
      aria-busy="true"
      aria-live="polite"
      className="flex h-full min-h-[40vh] w-full items-center justify-center text-sm text-muted-foreground"
    >
      Carregando…
    </div>
  )
}

/** Helper que envolve um elemento com `<Suspense fallback={...}>`. */
function lazy(element: React.ReactNode): React.ReactElement {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>
}

// ───────────────────────────────────────────────────────────────────────────
// Definição das rotas — exportada para reuso em testes (createMemoryRouter).
// ───────────────────────────────────────────────────────────────────────────

export const routes = [
  // Rota pública — fora do ProtectedRoute, sem AppShell.
  {
    path: '/login',
    element: lazy(<LoginPage />),
  },

  // Rotas autenticadas — envolvidas por ProtectedRoute + AppShell.
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: lazy(<DashboardPage />) },

      { path: '/test-cases', element: lazy(<TestCaseListPage />) },
      { path: '/test-cases/new', element: lazy(<TestCaseEditorPage />) },
      { path: '/test-cases/:id', element: lazy(<TestCaseEditorPage />) },
      { path: '/test-cases/:id/versions', element: lazy(<TestCaseVersionsPage />) },

      { path: '/test-suites', element: lazy(<TestSuiteListPage />) },
      { path: '/test-suites/new', element: lazy(<TestSuiteEditorPage />) },
      { path: '/test-suites/:id', element: lazy(<TestSuiteEditorPage />) },
      { path: '/test-suites/:id/versions', element: lazy(<TestSuiteVersionsPage />) },
      { path: '/executions/suites/:id', element: lazy(<SuiteRunDetailPage />) },
      { path: '/suite-runs/:id', element: lazy(<SuiteRunDetailPage />) },

      { path: '/datasets', element: lazy(<DatasetListPage />) },
      { path: '/datasets/new', element: lazy(<DatasetEditorPage />) },
      { path: '/datasets/import', element: lazy(<DatasetImportPage />) },
      { path: '/datasets/:id', element: lazy(<DatasetEditorPage />) },
      { path: '/datasets/:id/versions', element: lazy(<DatasetVersionsPage />) },

      { path: '/functions', element: lazy(<FunctionsPage />) },

      // Curadoria de base de conhecimento (ler = can_view_reports;
      // adjudicar = can_execute_test, checado no backend e por PermissionGate).
      { path: '/curadoria', element: lazy(<CuradoriaPage />) },
      { path: '/curadoria/:corpusId', element: lazy(<PainelPage />) },
      { path: '/curadoria/:corpusId/fila', element: lazy(<FilaAdjudicacaoPage />) },
      { path: '/curadoria/:corpusId/documentos', element: lazy(<DocumentosPage />) },
      {
        path: '/curadoria/:corpusId/documentos/:documentId',
        element: lazy(<DocumentoPage />),
      },
      {
        path: '/curadoria/:corpusId/documentos/:documentId/estrutura',
        element: lazy(<EstruturaPage />),
      },
      // Publicação controlada: ler = can_view_reports; decidir =
      // can_approve_curation (checado no backend e por PermissionGate na tela).
      { path: '/curadoria/:corpusId/aprovacao', element: lazy(<FilaAprovacaoPage />) },
      { path: '/curadoria/:corpusId/corpus', element: lazy(<CorpusPage />) },
      {
        path: '/curadoria/:corpusId/documentos/:documentId/revisao',
        element: lazy(<RevisaoPage />),
      },
      {
        path: '/curadoria/:corpusId/documentos/:documentId/projecoes',
        element: lazy(<ProjecoesPage />),
      },
      { path: '/app-profiles', element: lazy(<AppProfilesPage />) },
      { path: '/journey-profiles', element: lazy(<JourneyProfilesPage />) },

      // Devices (gestão de frota — item 10). Fora de /admin; exige can_view_tests.
      {
        path: '/devices',
        element: (
          <PermissionRoute flag="can_view_tests">
            {lazy(<DevicesPage />)}
          </PermissionRoute>
        ),
      },

      { path: '/schedules', element: lazy(<SchedulesPage />) },

      { path: '/executions', element: lazy(<ExecutionListPage />) },
      { path: '/executions/:id', element: lazy(<ExecutionDetailPage />) },
      { path: '/observability', element: lazy(<ObservabilityPage />) },

      { path: '/preview', element: lazy(<PreviewPage />) },
      { path: '/billing', element: lazy(<BillingPage />) },

      { path: '/settings/users', element: lazy(<UsersPage />) },
      { path: '/settings/profiles', element: lazy(<ProfilesPage />) },
      { path: '/settings/api-keys', element: lazy(<ApiKeysPage />) },

      { path: '/voice-samples', element: lazy(<VoiceSamplesPage />) },

      // Imports — exige `can_create_test`.
      {
        path: '/imports',
        element: (
          <PermissionRoute flag="can_create_test">
            {lazy(<ImportPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: '/imports/test-cases',
        element: (
          <PermissionRoute flag="can_create_test">
            {lazy(<ImportTestCasesPage />)}
          </PermissionRoute>
        ),
      },
      {
        path: '/imports/test-suite',
        element: (
          <PermissionRoute flag="can_create_test">
            {lazy(<ImportTestSuitePage />)}
          </PermissionRoute>
        ),
      },
      {
        path: '/imports/free-text',
        element: (
          <PermissionRoute flag="can_create_test">
            {lazy(<ImportFreeTextPage />)}
          </PermissionRoute>
        ),
      },

      // Admin global — exige `auth.mode === 'basic'`.
      {
        path: '/admin/organizations',
        element: <AdminGuard>{lazy(<OrganizationsPage />)}</AdminGuard>,
      },
      {
        path: '/admin/tenants',
        element: <AdminGuard>{lazy(<TenantsPage />)}</AdminGuard>,
      },
      {
        path: '/admin/llm',
        element: <AdminGuard>{lazy(<LLMPage />)}</AdminGuard>,
      },
      {
        path: '/admin/workers',
        element: <AdminGuard>{lazy(<WorkersPage />)}</AdminGuard>,
      },
      {
        path: '/admin/users',
        element: <AdminGuard>{lazy(<AdminUsersPage />)}</AdminGuard>,
      },
      {
        path: '/admin/billing',
        element: <AdminGuard>{lazy(<AdminBillingPage />)}</AdminGuard>,
      },
      {
        path: '/admin/access-events',
        element: <AdminGuard>{lazy(<AccessEventsPage />)}</AdminGuard>,
      },
    ],
  },

  // Catch-all → redireciona para `/`. O `ProtectedRoute` lá decide se
  // exibe Dashboard (autenticado) ou redireciona para `/login`.
  { path: '*', element: <Navigate to="/" replace /> },
]

const router = createBrowserRouter(routes)

// ───────────────────────────────────────────────────────────────────────────
// Bootstrap
// ───────────────────────────────────────────────────────────────────────────

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found in index.html')
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App>
      <RouterProvider router={router} />
    </App>
  </React.StrictMode>,
)
